import clsx from 'clsx'
import { useCallback } from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'

import { reorderArray } from '../../utils'
import { Icon, IconNameAndCategoryString } from '../Icon'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { RemovableItemContainer } from './RemovableItemContainer'

type Props<T extends CustomOption> = {
    options: T[]
    onDragEnd: (source: T, dest: T, list: T[]) => void
    children?: React.ReactNode
    droppableId?: string // used to identify the list droppable element. if more than one list is used, each list should have a unique name.
    isDragDisabled?: boolean
    shouldAlignDeleteButtonWithContainingCard?: boolean
}

type CustomOption = {
    id: string // must be unique
    title: string
    subtitle?: string
    seqnum: number
    onClick?: () => void
    onDelete?: () => void
} & ({ uri?: string } | { icon?: IconNameAndCategoryString })

function CustomOptionDisplay({
    option,
    disabled,
    shouldAlignDeleteButtonWithContainingCard,
}: {
    option: CustomOption
    disabled?: boolean
    shouldAlignDeleteButtonWithContainingCard: boolean
}) {
    let image: JSX.Element | null = null

    if ('uri' in option) {
        image = (
            <img
                src={option.uri}
                alt={option.title}
                className={clsx(
                    'size-9 rounded-full border-2 border-white',
                    disabled && 'opacity-50'
                )}
            />
        )
    }
    else if ('icon' in option && option.icon) {
        image = (
            <Icon
                icon={option.icon}
                background="round"
                className={clsx('size-[32px] border-2 border-white', disabled && 'opacity-50')}
            />
        )
    }

    return (
        <RemovableItemContainer
            onRemove={option.onDelete}
            deleteButtonMargin={shouldAlignDeleteButtonWithContainingCard ? undefined : '-right-6'}
        >
            <div
                onClick={option.onClick}
                className={clsx(
                    'relative flex w-full items-center gap-x-4 rounded-lg border border-gray-300 bg-gray-100 px-4 py-3',
                    option.onClick !== undefined &&
                        'cursor-pointer hover:bg-linear-to-b hover:from-black/4 hover:to-black/4'
                )}
            >
                <Icon
                    icon="drag.large"
                    className={clsx('z-10 cursor-grab! text-gray-500', disabled && 'text-gray-300')}
                />
                {image}
                <div className="flex flex-col gap-y-1">
                    <Label size="sm" className={clsx('text-left', disabled && 'text-gray-400')}>
                        {option.title}
                    </Label>
                    {Boolean(option.subtitle) && (
                        <Paragraph
                            size="sm"
                            className={clsx(
                                'text-left',
                                { 'text-gray-600': !disabled },
                                { 'text-gray-400': disabled }
                            )}
                        >
                            {option.subtitle}
                        </Paragraph>
                    )}
                </div>
            </div>
        </RemovableItemContainer>
    )
}

export function ArrangeableCustomOptions<T extends CustomOption>({
    options,
    onDragEnd,
    droppableId,
    isDragDisabled,
    shouldAlignDeleteButtonWithContainingCard = false,
}: Props<T>) {
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return

            const source = options[result.source.index]
            const dest = options[result.destination.index]

            const list = reorderArray(options, result.source.index, result.destination!.index)

            onDragEnd(source, dest, list)
        },
        [options, onDragEnd]
    )

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
                droppableId={droppableId ?? 'arrangeable-custom-options-droppable'}
                isCombineEnabled
            >
                {(provided, droppableSnapshot) => {
                    return (
                        <div
                            ref={provided.innerRef}
                            className={clsx('flex flex-col gap-y-3')}
                            {...provided.droppableProps}
                        >
                            {options.map((option, idx) => (
                                <Draggable
                                    key={option.id}
                                    draggableId={option.id}
                                    index={idx}
                                    isDragDisabled={isDragDisabled}
                                >
                                    {(provided, snapshot) => {
                                        return (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={clsx({
                                                    'opacity-50':
                                                        droppableSnapshot.isDraggingOver &&
                                                        !snapshot.isDragging,
                                                })}
                                            >
                                                <CustomOptionDisplay
                                                    option={option}
                                                    disabled={isDragDisabled}
                                                    shouldAlignDeleteButtonWithContainingCard={
                                                        shouldAlignDeleteButtonWithContainingCard
                                                    }
                                                />
                                            </div>
                                        )
                                    }}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )
                }}
            </Droppable>
        </DragDropContext>
    )
}
